// components/InventoryEdit.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMessage } from "../contexts/MessageContext";
import { Loading } from "./common/Loading";
import InventoryProductPhotosSection from "./InventoryProductPhotosSection";
import {
  stringArrayToLines,
  lineSeparatedToStringArray,
  catalogPhotosFromInventoryApi,
  buildInventoryImagesSubmitBody,
  defaultProductPhotoAlt,
  normalizeCatalogImageUrlFrontend,
  specRowsToPayload,
  specPayloadToRows,
} from "../utils/inventoryForm";

const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const InventoryEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [tagsText, setTagsText] = useState("");
  const [highlightsText, setHighlightsText] = useState("");
  const [catalogPhotos, setCatalogPhotos] = useState([]);
  const [pendingRemovedS3Keys, setPendingRemovedS3Keys] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [specsRows, setSpecsRows] = useState([{ label: "", value: "" }]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const { logout } = useAuth();
  const { showSuccess, showError } = useMessage();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      logout();
      navigate("/login");
    }
    const fetchItem = async () => {
      try {
        const res = await fetch(`${apiUrl}/inventory/${id}`, {
          headers: { "x-auth-token": token },
        });
        const data = await res.json();
        if (res.ok) {
          setItem(data);
          setTagsText(stringArrayToLines(data.tags));
          setHighlightsText(stringArrayToLines(data.highlights));
          setCatalogPhotos(catalogPhotosFromInventoryApi(data.images));
          setPendingRemovedS3Keys([]);
          setSpecsRows(specPayloadToRows(data.specs));
        } else {
          showError(data.message || "Error loading item");
          navigate("/inventory");
        }
        const resCat = await fetch(`${apiUrl}/category`, { headers: { "x-auth-token": token } });
        const dataCat = await resCat.json();
        if (resCat.ok) {
          setCategories(dataCat);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
    // Intentionally depend only on `id`: `showError`/`logout` used to get new identities each parent
    // render and re-ran this effect after upload toasts, refetching the item and wiping unsaved catalog photos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setSpec = (i, key, val) => {
    setSpecsRows((rows) => {
      const next = rows.slice();
      next[i] = { ...next[i], [key]: val };
      return next;
    });
  };

  const addSpecRow = () => setSpecsRows((r) => [...r, { label: "", value: "" }]);
  const removeSpecRow = (i) => {
    setSpecsRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, j) => j !== i)));
  };

  const removeCatalogPhoto = (clientId) => {
    const row = catalogPhotos.find((p) => p.clientId === clientId);
    if (!row) return;
    if (row.s3Key) setPendingRemovedS3Keys((prev) => [...prev, row.s3Key]);
    setCatalogPhotos((rows) => rows.filter((r) => r.clientId !== clientId));
  };

  const updateCatalogPhotoAlt = (clientId, alt) => {
    setCatalogPhotos((rows) =>
      rows.map((r) => (r.clientId === clientId ? { ...r, alt: String(alt) } : r))
    );
  };

  const uploadProductImages = async (files) => {
    if (!files?.length) return;
    const token = localStorage.getItem("token");
    if (!token) {
      showError("Please login again before uploading images.");
      return;
    }
    setUploading(true);
    try {
      const uploadedRows = [];
      for (const file of files) {
        const form = new FormData();
        form.append("file", file, file.name);
        form.append("productName", (item && item.name ? String(item.name) : "").trim());
        const res = await fetch(`${apiUrl}/inventory/product-upload`, {
          method: "POST",
          headers: { "x-auth-token": token },
          body: form,
        });
        const rawText = await res.text();
        let body = {};
        try {
          body = rawText ? JSON.parse(rawText) : {};
        } catch {
          throw new Error(`Upload failed (HTTP ${res.status}). Could not parse API response.`);
        }
        if (!res.ok || !body?.success || !body?.data?.imageUrl) {
          throw new Error(body?.message || `Failed to upload ${file.name}`);
        }
        const imageUrl = normalizeCatalogImageUrlFrontend(body.data.imageUrl);
        if (!imageUrl) {
          throw new Error(
            body?.message ||
              `Upload returned an unusable image URL (check PRODUCTS_ASSET_BASE_URL includes https://)—${file.name}`
          );
        }
        const apiAlt = typeof body.data.alt === "string" ? body.data.alt.trim() : "";
        uploadedRows.push({
          clientId: typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${imageUrl}-${Date.now()}-${Math.random()}`,
          url: imageUrl,
          alt: apiAlt || defaultProductPhotoAlt(item?.name || ""),
          s3Key: typeof body.data.key === "string" ? body.data.key : null,
        });
      }
      if (uploadedRows.length > 0) {
        setCatalogPhotos((prev) => {
          const next = [...prev];
          for (const r of uploadedRows) {
            if (!next.some((x) => x.url === r.url)) next.push(r);
          }
          return next;
        });
      }
      showSuccess(
        `Uploaded ${uploadedRows.length} image${uploadedRows.length === 1 ? "" : "s"} to S3 — shown below. Click Save to persist and apply S3 deletions for removed rows.`
      );
    } catch (error) {
      console.error(error);
      showError(error.message || "Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const buildPayload = () => {
    if (!item) return null;
    const { images, removedProductS3Keys } = buildInventoryImagesSubmitBody(
      catalogPhotos,
      pendingRemovedS3Keys,
      item?.name || ""
    );
    return {
      name: item.name,
      description: item.description,
      shortDescription: item.shortDescription,
      longDescription: item.longDescription,
      slug: item.slug,
      categoryLabel: item.categoryLabel,
      quantity: Math.max(0, Math.floor(Number(item.quantity) || 0)),
      price: Number(item.price) || 0,
      purchasePrice: Number(item.purchasePrice) || 0,
      categoryId: item.categoryId || null,
      rating: Math.min(5, Math.max(0, Number(item.rating) || 0)),
      reviewCount: Math.max(0, Math.floor(Number(item.reviewCount) || 0)),
      isActive: Boolean(item.isActive),
      tags: lineSeparatedToStringArray(tagsText),
      highlights: lineSeparatedToStringArray(highlightsText),
      images,
      removedProductS3Keys,
      specs: specRowsToPayload(specsRows),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    if (!payload) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${apiUrl}/inventory/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        let data = {};
        try {
          data = await res.json();
        } catch {
          /* ignore */
        }
        if (data && data.uuid) {
          setItem(data);
          setTagsText(stringArrayToLines(data.tags));
          setHighlightsText(stringArrayToLines(data.highlights));
          setCatalogPhotos(catalogPhotosFromInventoryApi(data.images));
          setPendingRemovedS3Keys([]);
          setSpecsRows(specPayloadToRows(data.specs));
        }
        const imgCount = Array.isArray(data.images) ? data.images.length : payload.images?.length ?? 0;
        showSuccess(
          imgCount
            ? `Item saved with ${imgCount} image URL${imgCount === 1 ? "" : "s"}.`
            : "Item updated successfully."
        );
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        let data = {};
        try {
          data = await res.json();
        } catch {
          /* ignore */
        }
        showError(data.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      showError(err.message || "Update failed");
    }
  };

  if (loading) return <Loading text="Loading item…" />;
  if (!item) return null;

  const inputCls =
    "w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";
  const sectionTitle = "text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4";

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 max-w-4xl mx-auto bg-white m-5 p-6 rounded shadow-md"
    >
      <h2 className="text-xl font-bold text-gray-800 mb-1">Edit item</h2>
      <p className="text-sm text-gray-500 mb-2">Update fields below; changes apply to the rental catalog and storefront.</p>
      <p className="text-sm text-gray-600 mb-6">
        Staff product ID{' '}
        <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{item.uuid}</code>
        {item.slug ? (
          <>
            {' '}
            · Public catalog slug{' '}
            <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{item.slug}</code>
          </>
        ) : null}
      </p>
        <div className="space-y-4">
          <h3 className={sectionTitle}>Core</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name</label>
              <input
                type="text"
                value={item.name || ""}
                onChange={(e) => setItem({ ...item, name: e.target.value })}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Slug</label>
              <input
                type="text"
                value={item.slug || ""}
                onChange={(e) => setItem({ ...item, slug: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select
                value={item.categoryId || ""}
                onChange={(e) => setItem({ ...item, categoryId: e.target.value })}
                className={inputCls}
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.uuid} value={cat.uuid}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Display category override (legacy)</label>
              <input
                type="text"
                value={item.categoryLabel || ""}
                onChange={(e) => setItem({ ...item, categoryLabel: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Quantity</label>
              <input
                type="number"
                min="0"
                value={item.quantity}
                onChange={(e) => setItem({ ...item, quantity: e.target.value })}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Rental price</label>
              <input
                type="number"
                step="0.01"
                value={item.price}
                onChange={(e) => setItem({ ...item, price: e.target.value })}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Purchase price</label>
              <input
                type="number"
                step="0.01"
                value={item.purchasePrice}
                onChange={(e) => setItem({ ...item, purchasePrice: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="flex flex-col justify-end">
              <label className={labelCls}>Active on public catalog</label>
              <div className="flex items-center gap-2 min-h-[42px]">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={!!item.isActive}
                  onChange={(e) => setItem({ ...item, isActive: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Item is visible in the catalog
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Description (short internal / legacy)</label>
              <input
                type="text"
                value={item.description || ""}
                onChange={(e) => setItem({ ...item, description: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className={sectionTitle}>Storefront text</h3>
          <div className="space-y-4">
          <label className={labelCls}>Short description</label>
          <textarea
            value={item.shortDescription || ""}
            onChange={(e) => setItem({ ...item, shortDescription: e.target.value })}
            className={inputCls}
            rows={2}
          />
          <label className={labelCls}>Long description</label>
          <textarea
            value={item.longDescription || ""}
            onChange={(e) => setItem({ ...item, longDescription: e.target.value })}
            className={inputCls}
            rows={5}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div>
              <label className={labelCls}>Rating (0–5)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={item.rating}
                onChange={(e) => setItem({ ...item, rating: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Review count</label>
              <input
                type="number"
                min="0"
                value={item.reviewCount}
                onChange={(e) => setItem({ ...item, reviewCount: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className={sectionTitle}>Tags &amp; highlights</h3>
          <label className={labelCls}>Tags (comma or newline)</label>
          <textarea value={tagsText} onChange={(e) => setTagsText(e.target.value)} className={inputCls} rows={2} />
          <label className={labelCls}>Highlights (one per line)</label>
          <textarea
            value={highlightsText}
            onChange={(e) => setHighlightsText(e.target.value)}
            className={inputCls}
            rows={3}
          />
        </div>

        <div className="space-y-4">
          <h3 className={sectionTitle}>Product photos (catalog)</h3>
          <InventoryProductPhotosSection
            photos={catalogPhotos}
            onRemove={removeCatalogPhoto}
            onChangePhotoAlt={updateCatalogPhotoAlt}
            uploading={uploading}
            onFileInputChange={async (e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = "";
              await uploadProductImages(files);
            }}
            helpText={
              <p>
                Upload-only. Files go to S3 under <code className="text-xs bg-gray-100 px-1 rounded">products/</code>. Folder:{' '}
                <strong>Settings → Applications → Catalog assets</strong>. Public read is required for website thumbnails. Max
                15 MB per file. <strong>Remove</strong> queues deletion from S3 when you click <strong>Save</strong>.
              </p>
            }
          />
        </div>

        <div className="space-y-4">
          <h3 className={sectionTitle}>Specs</h3>
          {specsRows.map((row, i) => (
            <div key={i} className="flex flex-wrap gap-2 items-end">
              <input
                className={`${inputCls} flex-1 min-w-[120px]`}
                placeholder="Label"
                value={row.label}
                onChange={(e) => setSpec(i, "label", e.target.value)}
              />
              <input
                className={`${inputCls} flex-1 min-w-[120px]`}
                placeholder="Value"
                value={row.value}
                onChange={(e) => setSpec(i, "value", e.target.value)}
              />
              <button
                type="button"
                className="px-3 py-1 text-xs bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                onClick={() => removeSpecRow(i)}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="text-sm text-blue-600 hover:underline"
            onClick={addSpecRow}
          >
            + Add spec row
          </button>
        </div>

        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => navigate("/inventory")}
            className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Go to inventory list
          </button>
        </div>
    </form>
  );
};

export default InventoryEdit;
